import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import config from '../config';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);

const cardStyle = {
  maxWidth: 760,
  margin: '40px auto',
  padding: 24,
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  background: '#fff',
  boxShadow: '0 8px 28px rgba(0,0,0,0.06)'
};

const lineStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  padding: '10px 0',
  borderBottom: '1px solid #f0f0f0'
};

const badgeStyle = (status) => {
  const bg =
    status === 'paid'
      ? '#ecfdf3'
      : status === 'failed' || status === 'cancelled'
      ? '#fef3f2'
      : '#fff7ed';

  const color =
    status === 'paid'
      ? '#027a48'
      : status === 'failed' || status === 'cancelled'
      ? '#b42318'
      : '#b54708';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 999,
    background: bg,
    color,
    fontWeight: 700,
    marginTop: 8
  };
};

export default function VivaReturnPage({ mode = 'success' }) {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [verifyData, setVerifyData] = useState(null);
  const [error, setError] = useState('');
  const [buzzResult, setBuzzResult] = useState(null); // {startedTrial, cardLast4}

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const transactionId = query.get('t') || query.get('transactionId') || '';
  const orderCode = query.get('s') || query.get('orderCode') || '';
  const eventId = query.get('eventId') || '';
  const lang = query.get('lang') || '';

  useEffect(() => {
    const run = async () => {
      if (!transactionId) {
        setError(tr('Missing Viva transaction id.'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const response = await axios.get(
          `${config.API_BASE_URL}/_payments/viva/verify`,
          {
            params: {
              t: transactionId,
              s: orderCode || undefined
            },
            withCredentials: true
          }
        );

        setVerifyData(response.data || null);

        // EstiaSync Buzz: ενεργοποίηση κάρτας (χρέωση 1 € με allowRecurring) → finalize server-side
        const lp = response.data?.localPayment;
        if (lp && String(lp.merchant_trns || '').startsWith('BUZZ_CARD|') &&
            lp.status === 'paid') {
          try {
            const fin = await axios.post(
              `${config.API_BASE_URL}/buzz/billing/card/finalize`,
              { transactionId, orderCode: lp.order_code },
              { withCredentials: true }
            );
            setBuzzResult(fin.data || null);
          } catch (e) {
            console.error('Buzz finalize error:', e);
          }
        }
      } catch (err) {
        console.error('Viva verify error:', err);
        setError(
          err?.response?.data?.error ||
            err?.message ||
            tr('Payment verification failed')
        );
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [transactionId, orderCode]);

  const localPayment = verifyData?.localPayment || null;
  const status = localPayment?.status || (mode === 'fail' ? 'failed' : 'pending');

  const title =
    status === 'paid'
      ? tr('Payment completed')
      : status === 'failed' || status === 'cancelled'
      ? tr('Payment was not completed')
      : tr('Payment verification in progress');

  const description =
    status === 'paid'
      ? tr('Your payment was verified successfully.')
      : status === 'failed' || status === 'cancelled'
      ? tr('The payment was cancelled or failed. You can try again from Billing.')
      : tr('We received your return from Viva and we are checking the final payment status.');

  return (
    <div className="responsive-container" style={{ padding: '20px' }}>
      <div style={cardStyle}>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>{title}</h2>
        <p style={{ marginTop: 0, color: '#4b5563' }}>{description}</p>

        {loading ? (
          <div style={{ padding: '16px 0' }}>{tr('Checking payment...')}</div>
        ) : error ? (
          <div
            style={{
              marginTop: 14,
              padding: '12px 14px',
              borderRadius: 10,
              background: '#fff1f0',
              border: '1px solid #ffccc7',
              color: '#a8071a'
            }}
          >
            {error}
          </div>
        ) : (
          <>
            <div style={badgeStyle(status)}>
              {status === 'paid'
                ? `✅ ${tr('Paid')}`
                : status === 'failed' || status === 'cancelled'
                ? `❌ ${tr('Failed')}`
                : `⏳ ${tr('Pending')}`}
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={lineStyle}>
                <strong>{tr('Order code')}</strong>
                <span>{localPayment?.order_code || orderCode || '-'}</span>
              </div>

              <div style={lineStyle}>
                <strong>{tr('Transaction id')}</strong>
                <span>{localPayment?.transaction_id || transactionId || '-'}</span>
              </div>

              <div style={lineStyle}>
                <strong>{tr('Amount')}</strong>
                <span>
                  {localPayment?.amount_minor !== undefined && localPayment?.amount_minor !== null
                    ? `${(Number(localPayment.amount_minor) / 100).toFixed(2)} ${localPayment?.currency_code || 'EUR'}`
                    : '-'}
                </span>
              </div>

              <div style={lineStyle}>
                <strong>{tr('Status')}</strong>
                <span>{localPayment?.status || '-'}</span>
              </div>

              <div style={lineStyle}>
                <strong>{tr('Event id')}</strong>
                <span>{eventId || '-'}</span>
              </div>

              <div style={lineStyle}>
                <strong>{tr('Language')}</strong>
                <span>{lang || '-'}</span>
              </div>
            </div>
          </>
        )}

        {buzzResult && (
          <div
            style={{
              marginTop: 16, padding: '14px 16px', borderRadius: 12,
              background: '#ecfdf3', border: '1px solid #a7f3d0', color: '#065f46'
            }}
          >
            <b>EstiaSync Buzz:</b>{' '}
            {buzzResult.startedTrial
              ? tr('Your card was verified and your 15-day trial has started!')
              : tr('Your card was updated successfully.')}{' '}
            {buzzResult.cardLast4 ? `(****${buzzResult.cardLast4})` : ''}
            <div style={{ marginTop: 10 }}>
              <Link to="/buzz/tables" style={{ fontWeight: 700, color: '#065f46' }}>
                → {tr("Continue to your tables")}
              </Link>
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 24
          }}
        >
          <Link
            to={buzzResult ? '/buzz/billing' : '/billing'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 160,
              padding: '10px 14px',
              borderRadius: 10,
              textDecoration: 'none',
              background: '#111827',
              color: '#fff',
              fontWeight: 700
            }}
          >
            {tr('Back to Billing')}
          </Link>

          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 160,
              padding: '10px 14px',
              borderRadius: 10,
              textDecoration: 'none',
              background: '#fff',
              border: '1px solid #d0d7de',
              color: '#111827',
              fontWeight: 700
            }}
          >
            {tr('Home')}
          </Link>
        </div>
      </div>
    </div>
  );
}